import type { PaymentMethod } from '~/common/enums/payments.enum';
import { PaymentType } from '~/common/enums/payments.enum';
import { depositPaymentTypeOptions } from '~/domains/payments/constants';
import type { useAvailablePaymentMethods } from '~/domains/payments/hooks/useAvailablePaymentMethods';

type AvailablePaymentMethods = ReturnType<
  typeof useAvailablePaymentMethods
>['availablePaymentMethods'];

type FilterDepositPaymentTypesInput = {
  availablePaymentMethods: AvailablePaymentMethods;
  showVIPP: boolean;
  showVenmo: boolean;
  isWireFlagReady: boolean;
  isEdgeBoostFlagReady: boolean;
  isCryptoDirectEnabled: boolean;
};

/**
 * Deposit tiles = registry order ∩ available-payment-methods (not hidden) ∩ feature flags.
 * WHOP_CRYPTO is removed when OPS_TOGGLE_CRYPTO_DIRECT is off. The legacy hosted-checkout
 * WHOP tile no longer exists on the deposit side (EDGE-32385): PaymentType.WHOP is not in
 * depositPaymentTypeOptions, so a WHOP availability row never produces a tile, and the
 * EDGE-32110 presence gate that used to hide it is gone with it.
 */
export function filterDepositPaymentTypes({
  availablePaymentMethods,
  showVIPP,
  showVenmo,
  isWireFlagReady,
  isEdgeBoostFlagReady,
  isCryptoDirectEnabled,
}: FilterDepositPaymentTypesInput): PaymentType[] {
  return depositPaymentTypeOptions
    .filter((option) => {
      const availablePaymentMethod = availablePaymentMethods?.find(
        (method) => method.paymentMethod === (option as unknown as PaymentMethod) // `availablePaymentMethods` is keyed by PaymentMethod while the tiles iterate PaymentType; the existing filter relies on the two enums sharing member names, and retyping it is out of scope (Appendix B)
      );

      if (!availablePaymentMethod) {
        return false;
      }

      return availablePaymentMethod.status !== 'hidden';
    })
    .filter((option) =>
      showVIPP
        ? option !== PaymentType.PAPER_CHECK
        : ![PaymentType.PAPER_CHECK, PaymentType.VIPPREFERRED].includes(option)
    )
    .filter((option) => showVenmo || option !== PaymentType.VENMO)
    .filter((option) => option !== PaymentType.WIRE || isWireFlagReady)
    // EDGE_BOOST is a ready-gate ONLY (mirrors WIRE): hidden until Split resolves, then present once
    // ready regardless of on/off. The on/off treatment governs its enabled state in DepositMethods,
    // not its presence. Folded verbatim from main's former inline DepositForm filter to preserve behavior.
    .filter((option) => option !== PaymentType.EDGE_BOOST || isEdgeBoostFlagReady)
    .filter((option) => isCryptoDirectEnabled || option !== PaymentType.WHOP_CRYPTO);
}
